/**
 * Fee Calculator - 费率计算工具
 * 
 * 处理费率优惠计算逻辑
 */

import type { User } from '../types/user.types';
import type { BrokerSettings } from '../types/brokerSettings.types';

/**
 * 推荐人优惠比例（0.2%）
 */
export const REFERRER_DISCOUNT = 0.002;  // 0.2%

/**
 * 计算实际充值费率（考虑推荐人优惠）
 * 
 * @param user - 用户信息
 * @param brokerSettings - 交易商设置
 * @returns 实际充值费率
 */
export function calculateDepositFeeRate(
  user: User | null,
  brokerSettings: BrokerSettings | null
): number {
  if (!brokerSettings) {
    return 0.03; // 默认 3%
  }

  const baseRate = brokerSettings.fees.deposit;
  
  // 检查是否有推荐人
  const hasReferrer = !!(user?.profile?.referrerCode);
  
  if (hasReferrer) {
    // 有推荐人，减少 0.2%
    return Math.max(0, baseRate - REFERRER_DISCOUNT);
  }
  
  return baseRate;
}

/**
 * 获取充值费率详情
 * 
 * @param user - 用户信息
 * @param brokerSettings - 交易商设置
 * @returns 费率详情对象
 */
export function getDepositFeeDetails(
  user: User | null,
  brokerSettings: BrokerSettings | null
): {
  baseRate: number;
  actualRate: number;
  hasDiscount: boolean;
  discountAmount: number;
  discountPercentage: number;
} {
  const baseRate = brokerSettings?.fees.deposit || 0.03;
  const hasDiscount = !!(user?.profile?.referrerCode);
  const discountAmount = hasDiscount ? REFERRER_DISCOUNT : 0;
  const actualRate = Math.max(0, baseRate - discountAmount);

  return {
    baseRate,
    actualRate,
    hasDiscount,
    discountAmount,
    discountPercentage: discountAmount * 100  // 转换为百分比
  };
}

/**
 * 计算充值后的实际到账金额
 * 
 * @param amount - 充值金额
 * @param user - 用户信息
 * @param brokerSettings - 交易商设置
 * @returns 到账金额详情
 */
export function calculateDepositAmount(
  amount: number,
  user: User | null,
  brokerSettings: BrokerSettings | null
): {
  originalAmount: number;
  feeRate: number;
  feeAmount: number;
  finalAmount: number;
  hasReferrerDiscount: boolean;
} {
  const feeRate = calculateDepositFeeRate(user, brokerSettings);
  const feeAmount = amount * feeRate;
  const finalAmount = amount - feeAmount;

  return {
    originalAmount: amount,
    feeRate,
    feeAmount,
    finalAmount,
    hasReferrerDiscount: !!(user?.profile?.referrerCode)
  };
}

/**
 * 格式化费率显示（百分比）
 * 
 * @param rate - 费率（小数形式）
 * @param decimals - 小数位数
 * @returns 格式化后的字符串
 */
export function formatFeeRate(rate: number, decimals: number = 2): string {
  return `${(rate * 100).toFixed(decimals)}%`;
}

